import { Injectable, inject } from '@angular/core';
import { AssignableRole } from '../../domain/models/assignable-role.model';
import { ClientAppAccessGrantInput, ClientAppAccount } from '../../domain/models/client-app-access.model';
import { LocalClient } from '../../domain/models/local-client.model';
import { LocalClientCreateInput } from '../../domain/models/local-client-create-input.model';
import { LocalClientDto } from '../dtos/local-client.dto';
import { RoleDto } from '../dtos/role.dto';
import { ClientAppInvitationPayload, CreateLocalClientPayload } from '../datasources/remote/local-clients.remote-datasource';
import { ClientMapper } from './client.mapper';

@Injectable()
export class LocalClientMapper {
  private readonly clientMapper = inject(ClientMapper);

  // Reuses `ClientMapper.toCreatePayload` (already the exact `CreateClientRequest` PascalCase
  // shape `POST /api/clients` expects) to build `ProviderProfile` -- the backend's
  // `LocalClientsService.CreateClientAsync` maps it to ProVet identically to the direct
  // `ClientsController.CreateClient` flow (see `CreateClientRequestProviderPayloadMapper`), so the
  // frontend payload shape must match 1:1 either way.
  public toCreatePayload(input: LocalClientCreateInput): CreateLocalClientPayload {
    return {
      Username: input.username,
      FirstName: input.firstName ?? input.providerProfile?.firstName ?? '',
      LastName: input.lastName ?? input.providerProfile?.lastName ?? '',
      RoleId: input.roleId,
      CreateInProvider: input.createInProvider,
      ...(input.providerProfile ? { ProviderProfile: this.clientMapper.toCreatePayload(input.providerProfile) } : {}),
    };
  }

  public toGrantAppAccessPayload(input: ClientAppAccessGrantInput): ClientAppInvitationPayload {
    return {
      Username: input.username,
      FirstName: input.firstName,
      LastName: input.lastName,
      LinkedProviderClientExternalId: input.providerClientId,
    };
  }

  public toLocalClient(dto: LocalClientDto): LocalClient {
    return {
      id: dto.PublicId,
      username: dto.Username,
      providerClientId: dto.ExternalClientId,
      homeOrganizationId: dto.OrganizationId,
    };
  }

  public toClientAppAccounts(dtos: readonly LocalClientDto[]): readonly ClientAppAccount[] {
    return dtos.map((dto) => ({
      clientId: dto.ClientId,
      username: dto.Username,
      providerClientId: dto.ExternalClientId,
      invitationPending: dto.InvitationPending,
      invitationDeliveryFailed: dto.InvitationDeliveryFailed,
    }));
  }

  public toAssignableRoles(dtos: readonly RoleDto[]): readonly AssignableRole[] {
    return dtos.map((dto) => ({ id: dto.Id, name: dto.Name }));
  }
}
